import '../lib/firebase-client'; // side-effect: ensures Firebase app is initialized
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  deleteDoc,
  setDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';

/**
 * General data access for firebase sdk (modular).
 */
class FirebaseRepository {
  db = null;

  constructor() {
    this.db = getFirestore();
  }

  /**
   * Build a CollectionReference, handling both top-level ("orgs")
   * and subcollection paths ("websites/abc123/keywords").
   */
  _collectionRef = (collectionName) => {
    const parts = collectionName.split('/');
    if (parts.length === 3) {
      return collection(doc(this.db, parts[0], parts[1]), parts[2]);
    }
    return collection(this.db, collectionName);
  };

  deleteDocumentsFromCollection = async (
    collectionName,
    attribute,
    value
  ) => {
    try {
      const q = query(this._collectionRef(collectionName), where(attribute, '==', value));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((docSnap) => {
        deleteDoc(docSnap.ref);
      });
    } catch (e) {
      console.log(e);
    }
  };

  deleteDocumentFromCollection = async (
    collectionName,
    documentId
  ) => {
    if (collectionName === 'sitemaps') {
      const q = query(this._collectionRef(collectionName), where('competitorId', '==', documentId));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((docSnap) => {
        deleteDoc(docSnap.ref);
      });
    } else {
      await deleteDoc(doc(this.db, collectionName, documentId));
    }
  };

  deleteSubcollectionFromCollection = async (
    parentCollection,
    childDocument,
    childCollection
  ) => {
    const subcollectionRef = collection(doc(this.db, parentCollection, childDocument), childCollection);
    const querySnapshot = await getDocs(subcollectionRef);
    querySnapshot.forEach((docSnap) => {
      deleteDoc(docSnap.ref);
    });
  };

  /**
   * @param  {string} collectionName
   * @param  {array} whereArrays - Array of arrays [['fieldName','queryOperator','value'],[]...]
   * @param {object} constraints {limit: number, orderBy: ['field', 'desc | asc']}
   * @returns {Object} - {success: boolean, data: [ {documents} ] }
   */
  queryCollection = async (
    collectionName,
    whereArrays,
    constraints
  ) => {
    const queryConstraints = [];

    if (whereArrays && whereArrays !== 'undefined' && whereArrays.length > 0) {
      for (let clauseArr of whereArrays) {
        queryConstraints.push(where(...clauseArr));
      }
    }

    if (constraints) {
      if (constraints.limit) {
        queryConstraints.push(firestoreLimit(constraints.limit));
      }
      if (constraints.orderBy) {
        queryConstraints.push(orderBy(...constraints.orderBy));
      }
    }

    let querySnap;
    let documents;
    try {
      const q = query(this._collectionRef(collectionName), ...queryConstraints);
      querySnap = await getDocs(q);
      documents = querySnap.docs.map((docSnap) => {
        return {
          ...docSnap.data(),
          id: docSnap.id
        };
      });
    } catch (error) {
      console.log(error);
    }
    return {
      docs: querySnap?.docs,
      size: querySnap?.size,
      empty: querySnap?.empty,
      success: true,
      data: documents
    };
  };

  updateDocument = async (collectionId, docId, data) => {
    await setDoc(doc(this.db, collectionId, docId), data, { merge: true });

    return {
      success: true,
      data: { ...data, id: docId },
    };
  };

  /**
   *
   * @param {*} collectionId
   * @param {*} docId
   * @returns {object} { docData }
   */
  getDocument = async (collectionId, docId) => {
    // fails silently if doc does not exist.
    const docSnap = await getDoc(doc(this.db, collectionId, docId));
    const document = { ...docSnap.data(), id: docId };

    return {
      success: true,
      data: document,
    };
  };

  async addDocument(collectionId, data) {
    const docRef = await addDoc(collection(this.db, collectionId), data);

    return {
      success: true,
      data: { ...data, id: docRef.id },
    };
  }

  async getFirebaseToken() {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken(/* forceRefresh */ true);
    return token;
  }

  getStorage() {
    return getStorage();
  }
}

const firebaseDbGateway = new FirebaseRepository();

export default firebaseDbGateway;
